import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Loader2, MapPin, Search } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { useAuditCustomers } from '@/hooks/useAudit';

/**
 * AuditCustomers — the driver's landing screen. With no search it lists ONLY
 * the tank-rental accounts that need a count; a search reaches any account on
 * tank rental billing. Nobody off the rental program is ever listed.
 */

export default function AuditCustomers() {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q), 250);
    return () => window.clearTimeout(t);
  }, [q]);
  const { data: customers, isLoading, error } = useAuditCustomers(debounced);

  return (
    <div>
      <h1 className="font-display text-xl font-bold tracking-display-tight text-brix-ink">
        Who are you at?
      </h1>
      <p className="mt-1 text-sm text-brix-muted">
        These customers on gas cylinder rental fees need a cylinder count. Search
        to count any other customer who rents cylinders.
      </p>

      <div className="relative mt-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brix-muted" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search cylinder rental customers…"
          className="h-12 pl-9 text-base"
          autoComplete="off"
        />
      </div>

      <div className="mt-4 space-y-2">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-brix-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading customers…</span>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-brix-border bg-brix-surface p-4 text-sm text-brix-warning">
            Couldn’t load customers: {error.message}
          </div>
        ) : (customers ?? []).length === 0 ? (
          <div className="rounded-xl border border-dashed border-brix-border bg-brix-surface p-6 text-center text-sm text-brix-muted">
            {debounced
              ? <>No cylinder rental customers match “{debounced}”.</>
              : <>No customers need a cylinder audit right now.</>}
          </div>
        ) : (
          (customers ?? []).map((c) => (
            <Link
              key={c.id ?? `qbo-${c.qbo_customer_id}`}
              to={c.id ? `/audit/${c.id}` : `/audit/qbo/${c.qbo_customer_id}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-brix-border bg-brix-surface p-4 shadow-card transition-shadow hover:shadow-card-hover"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate font-display text-sm font-semibold text-brix-ink">
                    {c.name}
                  </span>
                  {/* An account whose count has already been contradicted by a
                      pickup. Sorted to the top by the server — this is the
                      cheapest moment to clear one, while the driver is choosing
                      where to go. */}
                  {c.needs_audit ? (
                    <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
                      Needs a count
                    </span>
                  ) : null}
                </div>
                {c.needs_audit && c.audit_labels && c.audit_labels.length > 0 ? (
                  <div className="mt-0.5 truncate text-xs font-medium text-amber-700">
                    {c.audit_labels.join(', ')} {c.audit_labels.length === 1 ? "doesn't" : "don't"} add up
                  </div>
                ) : null}
                {c.address ? (
                  <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-brix-muted">
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span className="truncate">{c.address}</span>
                  </div>
                ) : null}
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-brix-muted" />
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
